import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { RestService } from 'src/app/services/rest.service';
import { detailsAdapter } from './new-details.adapter';
import { ModalController } from '@ionic/angular';
import { DebugService } from 'src/app/services/debug.service';
import { StatisticsService } from 'src/app/services/statistics.service';
import { AppDataService } from "src/app/services/appData.service";
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root'})

export class DetailsService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private rest = inject(RestService);
    private appData = inject(AppDataService);
    private stats = inject(StatisticsService);
    private modalController = inject(ModalController);
    private debugService = inject(DebugService);
    private translate = inject(TranslateService);
    private isModal = signal(false);
    private pointsData: any = [];
    private eventsData: any = [];
    private hikingsData: any = [];
    public itemDetails = signal(null);
    public gpxData = signal(null);

    setIsModal(value){
      this.isModal.set(value);
    }

    resetItemDetails(){
      this.itemDetails.set(null)
    }

    getEventDetailsServiceSignal(id: string) {
      if (this.eventsData?.[id]){
        this.debugService.info('Loading event cache data...', '[DetailesService]');
        this.setEvent(this.eventsData[id]);
        return;
      }

      return this.getEventDetails(id).pipe(
        map((response: any) => {
          this.debugService.debug(response, '[DetailesService] Events API');
          return detailsAdapter(response)
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleNotFound(error);
          return EMPTY;
        }))
        .subscribe((response: any) => {
          this.debugService.debug(response, '[DetailesService] Events API Mapped');
          this.eventsData[id] = response;
          this.setEvent(response);
        });
    }
    
    getEventDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-event-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleNotFound(error);
          return EMPTY;
        })
      );
    }

    setEvent(item){
      this.itemDetails.set(item);
      this.stats.addEvent(item.id);
      this.increaseEventsVisits(item.id);
    }
  
    getPointDetailsServiceSignal(id: string) {
      if (this.pointsData?.[id]){
        this.debugService.info('Loading point cache data...', '[DetailesService]');
        this.setPoint(this.pointsData[id]);
        return;
      }

      return this.getPointDetails(id)
        .pipe(
          map((response: any) => {
            this.debugService.debug(response, '[DetailesService] Point API');
            return detailsAdapter(response)
          }),
          catchError((error: HttpErrorResponse) => {
            this.handleNotFound(error);
            return EMPTY;
          }))
        .subscribe((response: any) => {
          this.debugService.debug(response, '[DetailesService] Point API Mapped');
          this.pointsData[id] = response;
          this.setPoint(response);
        });
    }
    
    getPointDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-point-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleNotFound(error);
          return EMPTY;
        })
      );
    }
      
    setPoint(item){
      this.itemDetails.set(item);
      this.stats.addPoint(item.id);
      this.increasePointVisits(item.id);
    }

    getHikingDetailsServiceSignal(id: string) {
      if (this.hikingsData?.[id]){
        this.debugService.info('Loading hiking cache data...', '[DetailesService]');
        this.setHiking(this.hikingsData?.[id]);
        return;
      }

      return this.getHikingDetails(id)
        .pipe(
          map((response: any) => {
            this.debugService.debug(response, '[DetailesService] Hiking API');
            return detailsAdapter(response)
          }),
          switchMap((details) =>  {
            const gpxUrl = details?.gpxFile?.[this.translate.currentLang]?.[0]?.src;
            if (!gpxUrl) 
              return of(details);
  
            return  this.loadGpxFromUrl(gpxUrl).pipe(
              tap((gpx) => this.gpxData.set(gpx)),
              map(() => details),
              catchError((err) => {
                console.warn('GPX failed, continuing without it', err);
                return of(details);
              })
            )
          }),
          catchError((error: HttpErrorResponse) => {
            this.handleNotFound(error);
            return EMPTY;
          }))
        .subscribe((response: any) => {
          this.debugService.debug(response, '[DetailesService] Hiking API Mapped');
          this.hikingsData[id] = response;
          this.setHiking(response);
        });
    }

    getHikingDetails(id: string): Observable<any> {
      const url = `${this.rest.apiUrlBase}/api/get-hiking-app-details/${id}`;
      return this.http.get<any>(url).pipe(
        tap((response: any) => {
          if (response.error)
            throw new HttpErrorResponse({error: response.error, status: 404, statusText: 'Not Found'});
        }),
        catchError((error: HttpErrorResponse) => {
          this.handleNotFound(error);
          return EMPTY;
        })
      );
    }

    setHiking(item){
      this.itemDetails.set(item);
      this.stats.addHiking(item.id);
      this.increaseHikingVisits(item.id);
    }
    
    handleNotFound(error: HttpErrorResponse) {
      console.log("HANDLING NOT HOUND")
      // if (error.status === 404 || error.status === 500) 
      if (this.isModal()){
        this.modalController.dismiss().catch(e => {
            console.warn("No modal found on dismiss")
        });
        return;
      }
      this.router.navigateByUrl('not-found');
    }

    increasePointVisits(id){
      this.rest.increaseVisitsPoint(id).subscribe((response) => {
        const point = this.appData?.dataHome?.homePoints?.find((point) => point?.items?.find(p => p.id == id))?.items?.find(e => e.id == id)
        if (!point) 
          return;

        point.num_clicks++;
      });
    }

    increaseEventsVisits(id){
      this.rest.increaseVisitsEvent(id).subscribe((response) => {
        const event = this.appData?.dataHome?.events?.find(e => e.id == id);
        if (!event) 
          return;

        event.num_clicks++;
      });
    }
    
    increaseHikingVisits(id){
      this.rest.increaseVisitsHiking(id).subscribe((response) => {
        const hiking = this.appData?.dataHome?.hikings?.find(h => h.id == id);
        if (!hiking) 
          return;

        hiking.num_clicks++;
      });
    }

  loadGpxFromUrl(url: string): Observable<string> {
    return this.http.get(url, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error("No se ha podido cargar archivo GPX");
        return of(null);
      })
    );
  }
}